import { supabase } from '../lib/supabase';

// Get all invoices for reporting
export const getAllInvoicesForReports = async (filters = {}) => {
  try {
    let query = supabase
      .from('invoices')
      .select('*')
      .order('date', { ascending: false });

    // Apply date filters if provided
    if (filters.startDate) {
      query = query.gte('date', filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte('date', filters.endDate);
    }
    if (filters.type) {
      query = query.eq('type', filters.type);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching invoices for reports:', error);
    throw error;
  }
};

// Get revenue statistics
export const getRevenueStats = async (startDate, endDate) => {
  try {
    let query = supabase
      .from('invoices')
      .select('total, status, date, type')
      .eq('type', 'invoice');

    if (startDate) {
      query = query.gte('date', startDate);
    }
    if (endDate) {
      query = query.lte('date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;

    const paid = data.filter(inv => inv.status === 'paid');
    const unpaid = data.filter(inv => inv.status !== 'paid');
    
    return {
      totalRevenue: paid.reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0),
      unpaidTotal: unpaid.reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0),
      paidCount: paid.length,
      unpaidCount: unpaid.length,
      totalCount: data.length,
    };
  } catch (error) {
    console.error('Error fetching revenue stats:', error);
    throw error;
  }
};

// Get estimate statistics
export const getEstimateStats = async (startDate, endDate) => {
  try {
    let query = supabase
      .from('invoices')
      .select('total, status, date')
      .eq('type', 'quote');

    if (startDate) {
      query = query.gte('date', startDate);
    }
    if (endDate) {
      query = query.lte('date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;

    const open = data.filter(q => q.status === 'draft');
    const finalized = data.filter(q => q.status !== 'draft');
    
    return {
      openCount: open.length,
      openTotal: open.reduce((sum, q) => sum + parseFloat(q.total || 0), 0),
      finalizedCount: finalized.length,
      finalizedTotal: finalized.reduce((sum, q) => sum + parseFloat(q.total || 0), 0),
      totalCount: data.length,
    };
  } catch (error) {
    console.error('Error fetching estimate stats:', error);
    throw error;
  }
};

// Get client statistics
export const getClientStats = async () => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('id');

    if (error) throw error;
    return {
      totalClients: (data || []).length,
    };
  } catch (error) {
    console.error('Error fetching client stats:', error);
    throw error;
  }
};

// Get revenue by month
export const getRevenueByMonth = async (months = 12) => {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - months);

    const { data, error } = await supabase
      .from('invoices')
      .select('total, date, status, type')
      .eq('type', 'invoice')
      .eq('status', 'paid')
      .gte('date', startDate.toISOString().split('T')[0])
      .lte('date', endDate.toISOString().split('T')[0])
      .order('date', { ascending: true });

    if (error) throw error;

    // Group by month
    const monthlyData = {};
    (data || []).forEach(invoice => {
      const date = new Date(invoice.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: monthKey, revenue: 0, count: 0 };
      }
      monthlyData[monthKey].revenue += parseFloat(invoice.total || 0);
      monthlyData[monthKey].count += 1;
    });

    return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
  } catch (error) {
    console.error('Error fetching revenue by month:', error);
    throw error;
  }
};

// Get top clients by revenue
export const getTopClientsByRevenue = async (limit = 10, startDate, endDate) => {
  try {
    let query = supabase
      .from('invoices')
      .select('total, client_id, status, date, clients(id, name)')
      .eq('type', 'invoice')
      .eq('status', 'paid');

    if (startDate) {
      query = query.gte('date', startDate);
    }
    if (endDate) {
      query = query.lte('date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Group by client
    const clientRevenue = {};
    (data || []).forEach(invoice => {
      const clientId = invoice.client_id;
      if (!clientRevenue[clientId]) {
        clientRevenue[clientId] = {
          clientId,
          clientName: invoice.clients?.name || 'Unknown',
          total: 0,
          count: 0,
        };
      }
      clientRevenue[clientId].total += parseFloat(invoice.total || 0);
      clientRevenue[clientId].count += 1;
    });

    return Object.values(clientRevenue)
      .sort((a, b) => b.total - a.total)
      .slice(0, limit);
  } catch (error) {
    console.error('Error fetching top clients:', error);
    throw error;
  }
};
